import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  ArrowLeft, CheckCircle, XCircle, Trash2, AlertCircle, Clock, ChevronDown, ChevronUp, Bot, Edit, Calendar, Save
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AITasks() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [editingTask, setEditingTask] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [schedulingTask, setSchedulingTask] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");

  useEffect(() => {
    if (actorId) {
      fetchTasks();
    }
  }, [actorId]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/admin/actors/${actorId}/ai-tasks?status=pending`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error("Failed to fetch tasks", error);
      toast.error("Failed to load AI tasks");
    } finally {
      setLoading(false);
    }
  };

  const approveTask = async (taskId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/actors/${actorId}/ai-tasks/${taskId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || "Task approved and executed");
      fetchTasks();
    } catch (error) {
      console.error("Failed to approve task", error);
      toast.error(error.response?.data?.detail || "Failed to approve task");
    }
  };

  const rejectTask = async (taskId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/actors/${actorId}/ai-tasks/${taskId}/reject`,
        { reason: "User rejected" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Task rejected");
      fetchTasks();
    } catch (error) {
      console.error("Failed to reject task", error);
      toast.error("Failed to reject task");
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/admin/actors/${actorId}/ai-tasks/${taskId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Task deleted");
      fetchTasks();
    } catch (error) {
      console.error("Failed to delete task", error);
      toast.error("Failed to delete task");
    }
  };

  const sendDraft = async (taskId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/actors/${actorId}/ai-tasks/${taskId}/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Email sent successfully!");
      fetchTasks();
    } catch (error) {
      console.error("Failed to send email", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const toggleExpand = (taskId) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const saveEdit = async (taskId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API}/admin/actors/${actorId}/ai-tasks/${taskId}`,
        { data: editedData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Draft updated successfully!");
      setEditingTask(null);
      fetchTasks();
    } catch (error) {
      console.error("Failed to update draft", error);
      toast.error("Failed to update draft");
    }
  };

  const scheduleEmail = async (taskId) => {
    try {
      // Create a local datetime object from user's input
      const localDateTime = new Date(`${scheduleDate}T${scheduleTime}:00`);
      
      // Convert to UTC ISO string for backend storage
      const utcDateTime = localDateTime.toISOString();
      
      const token = localStorage.getItem("token");
      
      await axios.post(
        `${API}/admin/actors/${actorId}/ai-tasks/${taskId}/schedule`,
        { scheduled_for: utcDateTime },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Email scheduled for ${scheduleDate} at ${scheduleTime} (your local time)`);
      setSchedulingTask(null);
      setScheduleDate("");
      setScheduleTime("");
      fetchTasks();
    } catch (error) {
      console.error("Failed to schedule email", error);
      toast.error("Failed to schedule email");
    }
  };

  const getActionColor = (actionType) => {
    switch (actionType) {
      case "delete": return "destructive";
      case "archive": return "secondary";
      case "tag": return "default";
      default: return "outline";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending": return <Clock className="w-4 h-4 text-yellow-500" />;
      case "scheduled": return <Calendar className="w-4 h-4 text-blue-500" />;
      case "approved": return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "rejected": return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  if (!actorId) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">No actor selected</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/ai/actors/${actorId}/emails`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Emails
          </Button>
          <div>
            <h1 className="text-2xl font-bold">AI Tasks</h1>
            <p className="text-sm text-muted-foreground">
              Review and approve AI-suggested actions
            </p>
          </div>
        </div>
      </div>

      {/* Task Count */}
      <div className="flex items-center gap-3">
        <Badge variant="outline" className="text-base px-4 py-2">
          📋 Pending Drafts: {tasks.length}
        </Badge>
        <p className="text-sm text-muted-foreground">
          Click on any card to expand/collapse
        </p>
      </div>

      {/* Tasks List */}
      {tasks.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No {statusFilter} tasks</h3>
            <p className="text-sm text-muted-foreground">
              {statusFilter === "pending" 
                ? "AI will create tasks here when it detects actions that need your approval"
                : `No ${statusFilter} tasks found`
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {tasks.map((task) => (
            <Card key={task.id} className="overflow-hidden">
              <CardHeader 
                className="pb-3 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(task.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusIcon(task.status)}
                      {task.type === "email_draft" || task.type === "email_reply" || task.type === "auto_draft_reply" ? (
                        <>
                          <Badge variant="default" className="bg-blue-600">
                            {task.type === "email_draft" ? "Email Draft" : 
                             task.type === "auto_draft_reply" ? "🤖 Auto-Draft" : "Email Reply"}
                          </Badge>
                          {task.auto_drafted && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                              Auto-Generated
                            </Badge>
                          )}
                          {task.status === "scheduled" && task.scheduled_for && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                              📅 Scheduled: {new Date(task.scheduled_for).toLocaleString()}
                            </Badge>
                          )}
                        </>
                      ) : (
                        <>
                          <Badge variant={getActionColor(task.action_type)}>
                            {task.action_type}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {task.task_type}
                          </Badge>
                        </>
                      )}
                    </div>
                    <CardTitle className="text-lg">
                      {task.type === "email_draft" || task.type === "email_reply" || task.type === "auto_draft_reply" ? (
                        task.data?.subject || "Email Draft"
                      ) : (
                        task.title
                      )}
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {task.type === "email_draft" || task.type === "email_reply" || task.type === "auto_draft_reply" ? (
                        <span>To: {task.data?.to}</span>
                      ) : (
                        task.description
                      )}
                    </CardDescription>
                  </div>
                  
                  {/* Expand/Collapse Icon */}
                  <div className="ml-4">
                    {expandedTasks.has(task.id) ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </div>
              </CardHeader>

              {/* Only show content if expanded */}
              {expandedTasks.has(task.id) && (
                <>
                  {task.status === "pending" && (task.type === "email_draft" || task.type === "email_reply" || task.type === "auto_draft_reply") && (
                    <div className="flex gap-2 px-6 pb-4" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTask(task.id);
                          setEditedData({
                            to: task.data.to,
                            subject: task.data.subject,
                            body: task.data.body || ""
                          });
                        }}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit Draft
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSchedulingTask(task.id);
                        }}
                      >
                        <Calendar className="w-4 h-4 mr-1" />
                        Schedule
                      </Button>
                      <Button
                        size="sm"
                        variant="default"
                        onClick={(e) => {
                          e.stopPropagation();
                          sendDraft(task.id);
                        }}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Send Now
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          rejectTask(task.id);
                        }}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Discard
                      </Button>
                    </div>
                  )}
                  
                  {task.status === "pending" && task.type !== "email_draft" && task.type !== "email_reply" && task.type !== "auto_draft_reply" && (
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => approveTask(task.id)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => rejectTask(task.id)}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  )}
                  
                  {task.status !== "pending" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteTask(task.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
              
              <CardContent>
                {/* Email Draft Preview */}
                {(task.type === "email_draft" || task.type === "email_reply" || task.type === "auto_draft_reply") && task.data && (
                  <div className="space-y-4">
                    {/* Show original email context for auto-drafts */}
                    {task.type === "auto_draft_reply" && task.data.original_email_subject && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs font-medium text-blue-900 mb-2">📧 Original Email</p>
                        <div className="text-sm space-y-1">
                          <div>
                            <span className="font-medium text-blue-800">From:</span> 
                            <span className="text-blue-700 ml-1">{task.data.original_email_from}</span>
                          </div>
                          <div>
                            <span className="font-medium text-blue-800">Subject:</span> 
                            <span className="text-blue-700 ml-1">{task.data.original_email_subject}</span>
                          </div>
                          {task.data.triggered_by_rule && (
                            <div className="text-xs text-blue-600 mt-2">
                              ⚡ Triggered by automation rule
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Draft preview or Edit mode */}
                    {editingTask === task.id ? (
                      // EDIT MODE
                      <div className="space-y-4 p-4 bg-white rounded-lg border-2 border-blue-300">
                        <div className="text-sm font-medium text-blue-700 mb-2">✏️ Editing Draft</div>
                        <div>
                          <label className="text-sm font-medium">To:</label>
                          <Input
                            value={editedData.to}
                            onChange={(e) => setEditedData({...editedData, to: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium">Subject:</label>
                          <Input
                            value={editedData.subject}
                            onChange={(e) => setEditedData({...editedData, subject: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium">Body:</label>
                          <Textarea
                            value={editedData.body}
                            onChange={(e) => setEditedData({...editedData, body: e.target.value})}
                            rows={12}
                            className="mt-1 font-mono text-sm"
                            placeholder="Enter email body (HTML supported)..."
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            💡 Tip: You can use HTML tags for formatting (e.g., &lt;b&gt;bold&lt;/b&gt;, &lt;p&gt;paragraph&lt;/p&gt;)
                          </p>
                        </div>
                        
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            onClick={() => saveEdit(task.id)}
                          >
                            <Save className="w-4 h-4 mr-1" />
                            Save Changes
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingTask(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // VIEW MODE
                      <div className="p-4 bg-gray-50 rounded-lg border">
                        <div className="space-y-2 text-sm">
                          <div>
                            <span className="font-medium">To:</span> {task.data.to}
                          </div>
                          <div>
                            <span className="font-medium">Subject:</span> {task.data.subject}
                          </div>
                          <div className="border-t pt-2 mt-2">
                            {/* HTML rendering with fallback to plain text */}
                            <div 
                              className="prose prose-sm max-w-none max-h-64 overflow-y-auto"
                              dangerouslySetInnerHTML={{ 
                                __html: task.data.html_body || (task.data.body || "").replace(/\n/g, '<br>') 
                              }}
                            />
                          </div>
                          {task.data.tone && (
                            <div className="text-xs text-muted-foreground">
                              <span className="font-medium">Tone:</span> {task.data.tone}
                            </div>
                          )}
                          {task.data.draft_instructions && (
                            <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
                              <span className="font-medium">Draft Instructions:</span> {task.data.draft_instructions}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                  <span>
                    {task.type === "email_draft" || task.type === "email_reply" ? (
                      "Email draft"
                    ) : (
                      `Affects ${task.affected_emails?.length || 0} emails`
                    )}
                  </span>
                  <span>{new Date(task.created_at).toLocaleString()}</span>
                </div>
                
                {/* Expandable details */}
                {task.analysis_details && (
                  <div className="mt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedTask(expandedTask === task.id ? null : task.id)}
                      className="w-full justify-between"
                    >
                      <span className="text-sm">View Details</span>
                      {expandedTask === task.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                    
                    {expandedTask === task.id && (
                      <div className="mt-3 p-3 bg-slate-50 rounded border space-y-2 max-h-64 overflow-y-auto">
                        {task.analysis_details.slice(0, 10).map((detail, idx) => (
                          <div key={idx} className="text-xs border-b pb-2">
                            <p className="font-semibold">{detail.from}</p>
                            <p className="text-muted-foreground">{detail.subject}</p>
                            <p className="text-xs mt-1">
                              Reasons: {detail.reasons?.join(", ")}
                            </p>
                            <Badge variant="outline" className="text-xs mt-1">
                              Confidence: {detail.confidence}%
                            </Badge>
                          </div>
                        ))}
                        {task.analysis_details.length > 10 && (
                          <p className="text-xs text-muted-foreground text-center pt-2">
                            ...and {task.analysis_details.length - 10} more
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {task.status === "approved" && task.execution_result && (
                  <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-sm">
                    ✓ Executed: {task.execution_result.executed} emails processed
                  </div>
                )}
                
                {task.status === "rejected" && task.rejection_reason && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm">
                    ✗ Rejected: {task.rejection_reason}
                  </div>
                )}
              </CardContent>
              </>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Schedule Modal */}
      {schedulingTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSchedulingTask(null)}>
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">📅 Schedule Email</h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-1">Date</label>
                <Input
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium block mb-1">Time</label>
                <Input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                💡 Email will be sent in your local timezone
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <Button
                onClick={() => scheduleEmail(schedulingTask)}
                disabled={!scheduleDate || !scheduleTime}
                className="flex-1"
              >
                <Calendar className="w-4 h-4 mr-1" />
                Schedule Send
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSchedulingTask(null);
                  setScheduleDate("");
                  setScheduleTime("");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
